<script setup>
import { defineEmits, defineProps, ref,  onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { to } from "await-to-js";
import { useEnvStore } from "@dutypay/front-end-design-system";
import { interceptor } from "@dutypay/front-end-design-system";
import { useTwoWayBinding } from "@dutypay/front-end-design-system";
import { useEMailAddressValidation } from "@dutypay/front-end-design-system/src/validations";
import { useRoute, useRouter } from "vue-router";

const { t } = useI18n();
const emit = defineEmits(["update", "go-to"]);
const props = defineProps({
  modelValue: { type: Object, required: true },
});
const loginData = useTwoWayBinding(props, emit, "modelValue");
const isLoading = ref(false);

async function sendAuthCodeIfUserExists() {
  const envStore = useEnvStore();
  isLoading.value = true;
  const { email } = loginData.value;
  let err;
  [err] = await to(interceptor.post(`${ envStore.apiUrls.dutypay }/api/v0/check-user`, { email }));
  if (err) {
    isLoading.value = false;
    return;
  }
  emit("go-to", "EmailOTPForm");
  isLoading.value = false;
}

onMounted(() => {
  const route = useRoute();
  const router = useRouter();
  // Clear route query params
  const query = Object.assign({}, route.query);
  delete query.emailAuthCode;
  delete query.email;
  router.replace({ query });
});

const validation = {
    email: [
        useEMailAddressValidation(),
    ]
}

</script>
<script>
export default {
  name: "EmailForm",
};
</script>

<template>
  <div>
    <h2>{{ t("sso.pages.LoginStages.EmailForm.emailForm.title") }}</h2>

    <DpForm
      v-loading="isLoading"
      class="dp-login-form"
      v-model="loginData"
      :showValidationNotification="false"
      :validation="validation"
      @submit="sendAuthCodeIfUserExists"
            >

            <DpInput
                v-model="loginData.email"
                :validation="validation.email"
                type="email"
                id="email"
                autocomplete="email"
                :label="t('sso.pages.LoginStages.EmailForm.email.input.label')"
                :placeholder="t('sso.pages.LoginStages.EmailForm.email.input.placeholder')"
                />
            <template #control="{ validate }">
                <DpButton
                    purpose="primaryAction"
                    size="large"
                    @click="validate">
                    {{ t("sso.pages.LoginStages.EmailForm.emailForm.buttonLabel") }}
                </DpButton>
            </template>
        </DpForm>
    </div>
</template>

<style lang="scss" scoped>
:deep(button.dp-button.dp-button__primary),
:deep(button.dp-button.dp-button__primary:visited) {
    &:not(.is-disabled) {
        -webkit-animation: color-change ease-in-out 57s infinite;
        -moz-animation: color-change ease-in-out 57s infinite;
        -o-animation: color-change ease-in-out 57s infinite;
        -ms-animation: color-change ease-in-out 57s infinite;
        animation: color-change ease-in-out 57s infinite;

        $color-zero: #409eff;
        $color-one: #e83158;
        $color-two: #db9800;
        $color-three: #9dcc4a;
        $color-four: #2c9fa8;
        $color-five: #099192;
        $color-six: #65adc1;
        $color-seven: #8b85db;
        $color-eight: #e09dea;
        $color-nine: #5399d3;

        @-webkit-keyframes color-change {
            0% {
                background-color: $color-zero;
            }
            10% {
                background-color: $color-one;
            }
            20% {
                background-color: $color-two;
            }
            30% {
                background-color: $color-three;
            }
            40% {
                background-color: $color-four;
            }
            50% {
                background-color: $color-five;
            }
            60% {
                background-color: $color-six;
            }
            70% {
                background-color: $color-seven;
            }
            80% {
                background-color: $color-eight;
            }
            90% {
                background-color: $color-nine;
            }
            100% {
                background-color: $color-zero;
            }
        }
        @-moz-keyframes color-change {
            0% {
                background-color: $color-zero;
            }
            10% {
                background-color: $color-one;
            }
            20% {
                background-color: $color-two;
            }
            30% {
                background-color: $color-three;
            }
            40% {
                background-color: $color-four;
            }
            50% {
                background-color: $color-five;
            }
            60% {
                background-color: $color-six;
            }
            70% {
                background-color: $color-seven;
            }
            80% {
                background-color: $color-eight;
            }
            90% {
                background-color: $color-nine;
            }
            100% {
                background-color: $color-zero;
            }
        }
        @-ms-keyframes color-change {
            0% {
                background-color: $color-zero;
            }
            10% {
                background-color: $color-one;
            }
            20% {
                background-color: $color-two;
            }
            30% {
                background-color: $color-three;
            }
            40% {
                background-color: $color-four;
            }
            50% {
                background-color: $color-five;
            }
            60% {
                background-color: $color-six;
            }
            70% {
                background-color: $color-seven;
            }
            80% {
                background-color: $color-eight;
            }
            90% {
                background-color: $color-nine;
            }
            100% {
                background-color: $color-zero;
            }
        }
        @-o-keyframes color-change {
            0% {
                background-color: $color-zero;
            }
            10% {
                background-color: $color-one;
            }
            20% {
                background-color: $color-two;
            }
            30% {
                background-color: $color-three;
            }
            40% {
                background-color: $color-four;
            }
            50% {
                background-color: $color-five;
            }
            60% {
                background-color: $color-six;
            }
            70% {
                background-color: $color-seven;
            }
            80% {
                background-color: $color-eight;
            }
            90% {
                background-color: $color-nine;
            }
            100% {
                background-color: $color-zero;
            }
        }
        @keyframes color-change {
            0% {
                background-color: $color-zero;
            }
            10% {
                background-color: $color-one;
            }
            20% {
                background-color: $color-two;
            }
            30% {
                background-color: $color-three;
            }
            40% {
                background-color: $color-four;
            }
            50% {
                background-color: $color-five;
            }
            60% {
                background-color: $color-six;
            }
            70% {
                background-color: $color-seven;
            }
            80% {
                background-color: $color-eight;
            }
            90% {
                background-color: $color-nine;
            }
            100% {
                background-color: $color-zero;
            }
        }
    }
}
</style>

<i18n>
{
  "ar-AE": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "خطأ"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "لا يوجد حساب DutyPay في HubSystem لعنوان البريد الإلكتروني هذا.",
          "title": "خطأ"
        }
      },
      "userDoesNotExist": {
        "message": "لا يوجد حساب DutyPay في HubSystem لعنوان البريد الإلكتروني هذا.",
        "title": "خطأ"
      }
    },
    "emailForm": {
      "title": "تسجيل الدخول",
      "buttonLabel": "تسجيل الدخول"
    },
    "email": {
      "input": {
        "placeholder": "أدخل عنوان البريد الإلكتروني",
        "label": "عنوان البريد الإلكتروني"
      }
    }
  },
  "ar-DZ": {
    "notification": {
      "hasUser": {
        "asyncError": {}
      },
      "checkUser": {
        "asyncError": {
          "title": "خطأ"
        }
      },
      "userDoesNotExist": {}
    },
    "emailForm": {
      "title": "تسجيل الدخول",
      "buttonLabel": "تسجيل الدخول"
    },
    "email": {
      "input": {
        "placeholder": "أدخل عنوان البريد الإلكتروني",
        "label": "عنوان البريد الإلكتروني"
      }
    }
  },
  "ar-EG": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "خطأ"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "لا يوجد حساب DutyPay في HubSystem لعنوان البريد الإلكتروني هذا.",
          "title": "خطأ"
        }
      },
      "userDoesNotExist": {
        "message": "لا يوجد حساب DutyPay في HubSystem لعنوان البريد الإلكتروني هذا.",
        "title": "خطأ"
      }
    },
    "emailForm": {
      "title": "تسجيل الدخول",
      "buttonLabel": "تسجيل الدخول"
    },
    "email": {
      "input": {
        "placeholder": "أدخل عنوان البريد الإلكتروني",
        "label": "عنوان البريد الإلكتروني"
      }
    }
  },
  "ar-LB": {
    "notification": {
      "hasUser": {
        "asyncError": {}
      },
      "checkUser": {
        "asyncError": {
          "title": "خطأ"
        }
      },
      "userDoesNotExist": {}
    },
    "emailForm": {
      "title": "تسجيل الدخول",
      "buttonLabel": "تسجيل الدخول"
    },
    "email": {
      "input": {
        "placeholder": "أدخل عنوان البريد الإلكتروني",
        "label": "عنوان البريد الإلكتروني"
      }
    }
  },
  "ar-MA": {
    "notification": {
      "hasUser": {
        "asyncError": {}
      },
      "checkUser": {
        "asyncError": {
          "title": "خطأ"
        }
      },
      "userDoesNotExist": {}
    },
    "emailForm": {
      "title": "تسجيل الدخول",
      "buttonLabel": "تسجيل الدخول"
    },
    "email": {
      "input": {
        "placeholder": "أدخل عنوان البريد الإلكتروني",
        "label": "عنوان البريد الإلكتروني"
      }
    }
  },
  "ar-SA": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "خطأ"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "لا يوجد حساب DutyPay في HubSystem لعنوان البريد الإلكتروني هذا.",
          "title": "خطأ"
        }
      },
      "userDoesNotExist": {
        "message": "لا يوجد حساب DutyPay في HubSystem لعنوان البريد الإلكتروني هذا.",
        "title": "خطأ"
      }
    },
    "emailForm": {
      "title": "تسجيل الدخول",
      "buttonLabel": "تسجيل الدخول"
    },
    "email": {
      "input": {
        "placeholder": "أدخل عنوان البريد الإلكتروني",
        "label": "عنوان البريد الإلكتروني"
      }
    }
  },
  "ar-SY": {
    "notification": {
      "hasUser": {
        "asyncError": {}
      },
      "checkUser": {
        "asyncError": {
          "title": "خطأ"
        }
      },
      "userDoesNotExist": {}
    },
    "emailForm": {
      "title": "تسجيل الدخول",
      "buttonLabel": "تسجيل الدخول"
    },
    "email": {
      "input": {
        "placeholder": "أدخل عنوان البريد الإلكتروني",
        "label": "عنوان البريد الإلكتروني"
      }
    }
  },
  "ar-TN": {
    "notification": {
      "hasUser": {
        "asyncError": {}
      },
      "checkUser": {
        "asyncError": {
          "title": "خطأ"
        }
      },
      "userDoesNotExist": {}
    },
    "emailForm": {
      "title": "تسجيل الدخول",
      "buttonLabel": "تسجيل الدخول"
    },
    "email": {
      "input": {
        "placeholder": "أدخل عنوان البريد الإلكتروني",
        "label": "عنوان البريد الإلكتروني"
      }
    }
  },
  "bg-BG": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "грешка"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Няма акаунт в DutyPay в HubSystem за този имейл адрес.",
          "title": "грешка"
        }
      },
      "userDoesNotExist": {
        "message": "Няма акаунт в DutyPay в HubSystem за този имейл адрес.",
        "title": "грешка"
      }
    },
    "emailForm": {
      "title": "вход",
      "buttonLabel": "вход"
    },
    "email": {
      "input": {
        "placeholder": "Въведете имейл адрес",
        "label": "Имейл адрес"
      }
    }
  },
  "bs-BA": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Greška"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Ne postoji DutyPay nalog u HubSystemu za ovu adresu e-pošte.",
          "title": "Greška"
        }
      },
      "userDoesNotExist": {
        "message": "Ne postoji DutyPay nalog u HubSystemu za ovu adresu e-pošte.",
        "title": "Greška"
      }
    },
    "emailForm": {
      "title": "Prijavi se",
      "buttonLabel": "Prijavi se"
    },
    "email": {
      "input": {
        "placeholder": "Unesi email adresu",
        "label": "Adresa e-pošte"
      }
    }
  },
  "cs-CZ": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Chyba"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Pro tuto e-mailovou adresu není v HubSystemu žádný účet DutyPay.",
          "title": "Chyba"
        }
      },
      "userDoesNotExist": {
        "message": "Pro tuto e-mailovou adresu není v HubSystemu žádný účet DutyPay.",
        "title": "Chyba"
      }
    },
    "emailForm": {
      "title": "Přihlášení",
      "buttonLabel": "Přihlášení"
    },
    "email": {
      "input": {
        "placeholder": "Zadejte emailovou adresu",
        "label": "E-mailová adresa"
      }
    }
  },
  "da-DK": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Fejl"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Der er ingen DutyPay-konto i HubSystemet for denne e-mailadresse.",
          "title": "Fejl"
        }
      },
      "userDoesNotExist": {
        "message": "Der er ingen DutyPay-konto i HubSystemet for denne e-mailadresse.",
        "title": "Fejl"
      }
    },
    "emailForm": {
      "title": "Log på",
      "buttonLabel": "Log på"
    },
    "email": {
      "input": {
        "placeholder": "Indtast emailadresse",
        "label": "E-mailadresse"
      }
    }
  },
  "de-DE": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Fehler"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Zu dieser E-Mail-Adresse existiert kein DutyPay-Konto im HubSystem.",
          "title": "Fehler"
        }
      },
      "userDoesNotExist": {
        "message": "Zu dieser E-Mail-Adresse existiert kein DutyPay-Konto im HubSystem.",
        "title": "Fehler"
      }
    },
    "emailForm": {
      "title": "Anmelden",
      "buttonLabel": "Anmelden"
    },
    "email": {
      "input": {
        "placeholder": "E-Mail-Adresse eingeben",
        "label": "E-Mail-Adresse"
      }
    }
  },
  "de-AT": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Fehler"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Zu dieser E-Mail-Adresse existiert kein DutyPay-Konto im HubSystem.",
          "title": "Fehler"
        }
      },
      "userDoesNotExist": {
        "message": "Zu dieser E-Mail-Adresse existiert kein DutyPay-Konto im HubSystem.",
        "title": "Fehler"
      }
    },
    "emailForm": {
      "title": "Anmelden",
      "buttonLabel": "Anmelden"
    },
    "email": {
      "input": {
        "placeholder": "E-Mail-Adresse eingeben",
        "label": "E-Mail-Adresse"
      }
    }
  },
  "de-CH": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Fehler"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Zu dieser E-Mail-Adresse existiert kein DutyPay-Konto im HubSystem.",
          "title": "Fehler"
        }
      },
      "userDoesNotExist": {
        "message": "Zu dieser E-Mail-Adresse existiert kein DutyPay-Konto im HubSystem.",
        "title": "Fehler"
      }
    },
    "emailForm": {
      "title": "Anmelden",
      "buttonLabel": "Anmelden"
    },
    "email": {
      "input": {
        "placeholder": "E-Mail-Adresse eingeben",
        "label": "E-Mail-Adresse"
      }
    }
  },
  "de-LU": {
    "notification": {
      "hasUser": {
        "asyncError": {}
      },
      "checkUser": {
        "asyncError": {
          "title": "Fehler"
        }
      },
      "userDoesNotExist": {}
    },
    "emailForm": {
      "title": "Anmelden",
      "buttonLabel": "Anmelden"
    },
    "email": {
      "input": {
        "placeholder": "E-Mail-Adresse eingeben",
        "label": "E-Mail-Adresse"
      }
    }
  },
  "el-GR": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Σφάλμα"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Δεν υπάρχει λογαριασμός DutyPay στο HubSystem για αυτήν τη διεύθυνση ηλεκτρονικού ταχυδρομείου.",
          "title": "Σφάλμα"
        }
      },
      "userDoesNotExist": {
        "message": "Δεν υπάρχει λογαριασμός DutyPay στο HubSystem για αυτήν τη διεύθυνση ηλεκτρονικού ταχυδρομείου.",
        "title": "Σφάλμα"
      }
    },
    "emailForm": {
      "title": "Σύνδεση",
      "buttonLabel": "Σύνδεση"
    },
    "email": {
      "input": {
        "placeholder": "Εισάγετε διεύθυνση email",
        "label": "Διεύθυνση ηλεκτρονικού ταχυδρομείου"
      }
    }
  },
  "el-CY": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Σφάλμα"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Δεν υπάρχει λογαριασμός DutyPay στο HubSystem για αυτήν τη διεύθυνση ηλεκτρονικού ταχυδρομείου.",
          "title": "Σφάλμα"
        }
      },
      "userDoesNotExist": {
        "message": "Δεν υπάρχει λογαριασμός DutyPay στο HubSystem για αυτήν τη διεύθυνση ηλεκτρονικού ταχυδρομείου.",
        "title": "Σφάλμα"
      }
    },
    "emailForm": {
      "title": "Σύνδεση",
      "buttonLabel": "Σύνδεση"
    },
    "email": {
      "input": {
        "placeholder": "Εισάγετε διεύθυνση email",
        "label": "Διεύθυνση ηλεκτρονικού ταχυδρομείου"
      }
    }
  },
  "en-GB": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Error"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "There is no DutyPay account in the HubSystem for this e-mail address.",
          "title": "Error"
        }
      },
      "userDoesNotExist": {
        "message": "There is no DutyPay account in the HubSystem for this e-mail address.",
        "title": "Error"
      }
    },
    "emailForm": {
      "title": "Login",
      "buttonLabel": "Login"
    },
    "email": {
      "input": {
        "placeholder": "Enter email address",
        "label": "Email Address"
      }
    }
  },
  "en-IE": {
    "notification": {
      "hasUser": {
        "asyncError": {}
      },
      "checkUser": {
        "asyncError": {
          "title": "Error"
        }
      },
      "userDoesNotExist": {}
    },
    "emailForm": {
      "title": "Login",
      "buttonLabel": "Login"
    },
    "email": {
      "input": {
        "placeholder": "Enter email address",
        "label": "Email Address"
      }
    }
  },
  "en-US": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Error"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "There is no DutyPay account in the HubSystem for this e-mail address.",
          "title": "Error"
        }
      },
      "userDoesNotExist": {
        "message": "There is no DutyPay account in the HubSystem for this e-mail address.",
        "title": "Error"
      }
    },
    "emailForm": {
      "title": "Login",
      "buttonLabel": "Login"
    },
    "email": {
      "input": {
        "placeholder": "Enter email address",
        "label": "Email Address"
      }
    }
  },
  "es-ES": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Error"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "No hay una cuenta de DutyPay en HubSystem para esta dirección de correo electrónico.",
          "title": "Error"
        }
      },
      "userDoesNotExist": {
        "message": "No hay una cuenta de DutyPay en HubSystem para esta dirección de correo electrónico.",
        "title": "Error"
      }
    },
    "emailForm": {
      "title": "Iniciar sesión",
      "buttonLabel": "Iniciar sesión"
    },
    "email": {
      "input": {
        "placeholder": "Introduzca dirección de correo electrónico",
        "label": "Dirección de correo electrónico"
      }
    }
  },
  "et-EE": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Viga"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Selle e-posti aadressi jaoks pole HubSystemis DutyPay kontot.",
          "title": "Viga"
        }
      },
      "userDoesNotExist": {
        "message": "Selle e-posti aadressi jaoks pole HubSystemis DutyPay kontot.",
        "title": "Viga"
      }
    },
    "emailForm": {
      "title": "Logi sisse",
      "buttonLabel": "Logi sisse"
    },
    "email": {
      "input": {
        "placeholder": "Sisesta e-posti aadress",
        "label": "E-posti aadress"
      }
    }
  },
  "fi-FI": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Virhe"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "HubSystemissä ei ole DutyPay-tiliä tälle sähköpostiosoitteelle.",
          "title": "Virhe"
        }
      },
      "userDoesNotExist": {
        "message": "HubSystemissä ei ole DutyPay-tiliä tälle sähköpostiosoitteelle.",
        "title": "Virhe"
      }
    },
    "emailForm": {
      "title": "Sisäänkirjautuminen",
      "buttonLabel": "Sisäänkirjautuminen"
    },
    "email": {
      "input": {
        "placeholder": "Syötä sähköpostiosoite",
        "label": "Sähköpostiosoite"
      }
    }
  },
  "fr-BE": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Erreur"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Il n'y a pas de compte DutyPay dans le HubSystem pour cette adresse e-mail.",
          "title": "Erreur"
        }
      },
      "userDoesNotExist": {
        "message": "Il n'y a pas de compte DutyPay dans le HubSystem pour cette adresse e-mail.",
        "title": "Erreur"
      }
    },
    "emailForm": {
      "title": "Connexion",
      "buttonLabel": "Connexion"
    },
    "email": {
      "input": {
        "placeholder": "Entrez l'adresse e-mail",
        "label": "Adresse e-mail"
      }
    }
  },
  "fr-CH": {
    "notification": {
      "hasUser": {
        "asyncError": {}
      },
      "checkUser": {
        "asyncError": {
          "title": "Erreur"
        }
      },
      "userDoesNotExist": {}
    },
    "emailForm": {
      "title": "Connexion",
      "buttonLabel": "Connexion"
    },
    "email": {
      "input": {
        "placeholder": "Entrez l'adresse e-mail",
        "label": "Adresse e-mail"
      }
    }
  },
  "fr-FR": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Erreur"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Il n'y a pas de compte DutyPay dans le HubSystem pour cette adresse e-mail.",
          "title": "Erreur"
        }
      },
      "userDoesNotExist": {
        "message": "Il n'y a pas de compte DutyPay dans le HubSystem pour cette adresse e-mail.",
        "title": "Erreur"
      }
    },
    "emailForm": {
      "title": "Connexion",
      "buttonLabel": "Connexion"
    },
    "email": {
      "input": {
        "placeholder": "Entrez l'adresse e-mail",
        "label": "Adresse e-mail"
      }
    }
  },
  "fr-LU": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Erreur"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Il n'y a pas de compte DutyPay dans le HubSystem pour cette adresse e-mail.",
          "title": "Erreur"
        }
      },
      "userDoesNotExist": {
        "message": "Il n'y a pas de compte DutyPay dans le HubSystem pour cette adresse e-mail.",
        "title": "Erreur"
      }
    },
    "emailForm": {
      "title": "Connexion",
      "buttonLabel": "Connexion"
    },
    "email": {
      "input": {
        "placeholder": "Entrez l'adresse e-mail",
        "label": "Adresse e-mail"
      }
    }
  },
  "ga-IE": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Earráid"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Níl aon chuntas DutyPay sa HubSystem don seoladh ríomhphoist seo.",
          "title": "Earráid"
        }
      },
      "userDoesNotExist": {
        "message": "Níl aon chuntas DutyPay sa HubSystem don seoladh ríomhphoist seo.",
        "title": "Earráid"
      }
    },
    "emailForm": {
      "title": "Logáil isteach",
      "buttonLabel": "Logáil isteach"
    },
    "email": {
      "input": {
        "placeholder": "Cuir isteach seoladh ríomhphoist",
        "label": "Seoladh ríomhphoist"
      }
    }
  },
  "he-IL": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "שְׁגִיאָה"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "אין חשבון DutyPay ב-HubSystem עבור כתובת דוא＂ל זו.",
          "title": "שְׁגִיאָה"
        }
      },
      "userDoesNotExist": {
        "message": "אין חשבון DutyPay ב-HubSystem עבור כתובת דוא＂ל זו.",
        "title": "שְׁגִיאָה"
      }
    },
    "emailForm": {
      "title": "התחברות",
      "buttonLabel": "התחברות"
    },
    "email": {
      "input": {
        "placeholder": "הזן כתובת דוא＂ל",
        "label": "כתובת דוא＂ל"
      }
    }
  },
  "hi-IN": {
    "notification": {
      "hasUser": {
        "asyncError": {}
      },
      "checkUser": {
        "asyncError": {
          "title": "त्रुटि"
        }
      },
      "userDoesNotExist": {}
    },
    "emailForm": {
      "title": "लॉग इन",
      "buttonLabel": "लॉग इन"
    },
    "email": {
      "input": {
        "placeholder": "ईमेल पता दर्ज करें",
        "label": "ईमेल पता"
      }
    }
  },
  "hr-HR": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Pogreška"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Ne postoji DutyPay račun u HubSystem za ovu adresu e-pošte.",
          "title": "Pogreška"
        }
      },
      "userDoesNotExist": {
        "message": "Ne postoji DutyPay račun u HubSystem za ovu adresu e-pošte.",
        "title": "Pogreška"
      }
    },
    "emailForm": {
      "title": "Prijavi se",
      "buttonLabel": "Prijavi se"
    },
    "email": {
      "input": {
        "placeholder": "Unesi email adresu",
        "label": "Adresa e-pošte"
      }
    }
  },
  "hu-HU": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Hiba"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Ehhez az e-mail címhez nincs DutyPay-fiók a HubSystemben.",
          "title": "Hiba"
        }
      },
      "userDoesNotExist": {
        "message": "Ehhez az e-mail címhez nincs DutyPay-fiók a HubSystemben.",
        "title": "Hiba"
      }
    },
    "emailForm": {
      "title": "Belépés",
      "buttonLabel": "Belépés"
    },
    "email": {
      "input": {
        "placeholder": "Adja meg az e-mail címet",
        "label": "E-mail-cím"
      }
    }
  },
  "it-CH": {
    "notification": {
      "hasUser": {
        "asyncError": {}
      },
      "checkUser": {
        "asyncError": {
          "title": "Errore"
        }
      },
      "userDoesNotExist": {}
    },
    "emailForm": {
      "title": "Accedi",
      "buttonLabel": "Accedi"
    },
    "email": {
      "input": {
        "placeholder": "Inserisci l'indirizzo email",
        "label": "Indirizzo email"
      }
    }
  },
  "it-IT": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Errore"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Non esiste un account DutyPay in HubSystem per questo indirizzo e-mail.",
          "title": "Errore"
        }
      },
      "userDoesNotExist": {
        "message": "Non esiste un account DutyPay in HubSystem per questo indirizzo e-mail.",
        "title": "Errore"
      }
    },
    "emailForm": {
      "title": "Accedi",
      "buttonLabel": "Accedi"
    },
    "email": {
      "input": {
        "placeholder": "Inserisci l'indirizzo email",
        "label": "Indirizzo e-mail"
      }
    }
  },
  "ja-JP": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "エラー"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "HubSystem には、このメール アドレスの DutyPay アカウントがありません。",
          "title": "エラー"
        }
      },
      "userDoesNotExist": {
        "message": "HubSystem には、このメール アドレスの DutyPay アカウントがありません。",
        "title": "エラー"
      }
    },
    "emailForm": {
      "title": "ログインする",
      "buttonLabel": "ログインする"
    },
    "email": {
      "input": {
        "placeholder": "メールアドレスを入力してください",
        "label": "電子メールアドレス"
      }
    }
  },
  "ko-KR": {
    "notification": {
      "hasUser": {
        "asyncError": {}
      },
      "checkUser": {
        "asyncError": {
          "title": "오류"
        }
      },
      "userDoesNotExist": {}
    },
    "emailForm": {
      "title": "로그인",
      "buttonLabel": "로그인"
    },
    "email": {
      "input": {
        "placeholder": "이메일 주소를 입력하세요",
        "label": "이메일 주소"
      }
    }
  },
  "lt-LT": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Klaida"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Šiam el. pašto adresui „HubSystem“ nėra „DutyPay“ paskyros.",
          "title": "Klaida"
        }
      },
      "userDoesNotExist": {
        "message": "Šiam el. pašto adresui „HubSystem“ nėra „DutyPay“ paskyros.",
        "title": "Klaida"
      }
    },
    "emailForm": {
      "title": "Prisijungti",
      "buttonLabel": "Prisijungti"
    },
    "email": {
      "input": {
        "placeholder": "Įveskite el. pašto adresą",
        "label": "Elektroninio pašto adresas"
      }
    }
  },
  "lv-LV": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Kļūda"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Šai e-pasta adresei HubSystem nav DutyPay konta.",
          "title": "Kļūda"
        }
      },
      "userDoesNotExist": {
        "message": "Šai e-pasta adresei HubSystem nav DutyPay konta.",
        "title": "Kļūda"
      }
    },
    "emailForm": {
      "title": "Pieslēgties",
      "buttonLabel": "Pieslēgties"
    },
    "email": {
      "input": {
        "placeholder": "Ievadiet e-pasta adresi",
        "label": "E-pasta adrese"
      }
    }
  },
  "mt-MT": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Żball"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "M'hemm l-ebda kont DutyPay fis-HubSystem għal dan l-indirizz elettroniku.",
          "title": "Żball"
        }
      },
      "userDoesNotExist": {
        "message": "M'hemm l-ebda kont DutyPay fis-HubSystem għal dan l-indirizz elettroniku.",
        "title": "Żball"
      }
    },
    "emailForm": {
      "title": "Illoggja",
      "buttonLabel": "Illoggja"
    },
    "email": {
      "input": {
        "placeholder": "Daħħal l-indirizz tal-email",
        "label": "Indirizz tal-posta elettronika"
      }
    }
  },
  "nb-NO": {
    "notification": {
      "hasUser": {
        "asyncError": {}
      },
      "checkUser": {
        "asyncError": {
          "title": "Feil"
        }
      },
      "userDoesNotExist": {}
    },
    "emailForm": {
      "title": "Logg inn",
      "buttonLabel": "Logg inn"
    },
    "email": {
      "input": {
        "placeholder": "Skriv inn epostadresse",
        "label": "E-postadresse"
      }
    }
  },
  "nl-BE": {
    "notification": {
      "hasUser": {
        "asyncError": {}
      },
      "checkUser": {
        "asyncError": {
          "title": "Fout"
        }
      },
      "userDoesNotExist": {}
    },
    "emailForm": {
      "title": "Inloggen",
      "buttonLabel": "Inloggen"
    },
    "email": {
      "input": {
        "placeholder": "Voer e-mailadres in",
        "label": "E-mailadres"
      }
    }
  },
  "nl-NL": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Fout"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Er is geen DutyPay-account in het HubSystem voor dit e-mailadres.",
          "title": "Fout"
        }
      },
      "userDoesNotExist": {
        "message": "Er is geen DutyPay-account in het HubSystem voor dit e-mailadres.",
        "title": "Fout"
      }
    },
    "emailForm": {
      "title": "Inloggen",
      "buttonLabel": "Inloggen"
    },
    "email": {
      "input": {
        "placeholder": "Voer e-mailadres in",
        "label": "E-mailadres"
      }
    }
  },
  "pl-PL": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Błąd"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Dla tego adresu e-mail nie ma konta DutyPay w HubSystem.",
          "title": "Błąd"
        }
      },
      "userDoesNotExist": {
        "message": "Dla tego adresu e-mail nie ma konta DutyPay w HubSystem.",
        "title": "Błąd"
      }
    },
    "emailForm": {
      "title": "Zaloguj się",
      "buttonLabel": "Zaloguj się"
    },
    "email": {
      "input": {
        "placeholder": "Wprowadź adres e-mail",
        "label": "Adres e-mail"
      }
    }
  },
  "pt-PT": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Erro"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Não há conta DutyPay no HubSystem para este endereço de e-mail.",
          "title": "Erro"
        }
      },
      "userDoesNotExist": {
        "message": "Não há conta DutyPay no HubSystem para este endereço de e-mail.",
        "title": "Erro"
      }
    },
    "emailForm": {
      "title": "Iniciar sessão",
      "buttonLabel": "Iniciar sessão"
    },
    "email": {
      "input": {
        "placeholder": "Digite o endereço de e-mail",
        "label": "Endereço de e-mail"
      }
    }
  },
  "ro-RO": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Eroare"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Nu există niciun cont DutyPay în HubSystem pentru această adresă de e-mail.",
          "title": "Eroare"
        }
      },
      "userDoesNotExist": {
        "message": "Nu există niciun cont DutyPay în HubSystem pentru această adresă de e-mail.",
        "title": "Eroare"
      }
    },
    "emailForm": {
      "title": "Conectare",
      "buttonLabel": "Conectare"
    },
    "email": {
      "input": {
        "placeholder": "Introduceți adresa de email",
        "label": "Adresa de e-mail"
      }
    }
  },
  "ru-RU": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Ошибка"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Для этого адреса электронной почты в системе HubSystem нет учетной записи DutyPay.",
          "title": "Ошибка"
        }
      },
      "userDoesNotExist": {
        "message": "Для этого адреса электронной почты в системе HubSystem нет учетной записи DutyPay.",
        "title": "Ошибка"
      }
    },
    "emailForm": {
      "title": "Вход",
      "buttonLabel": "Вход"
    },
    "email": {
      "input": {
        "placeholder": "Введите адрес электронной почты",
        "label": "Адрес электронной почты"
      }
    }
  },
  "sk-SK": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Chyba"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Pre túto e-mailovú adresu nie je v HubSystem žiadny účet DutyPay.",
          "title": "Chyba"
        }
      },
      "userDoesNotExist": {
        "message": "Pre túto e-mailovú adresu nie je v HubSystem žiadny účet DutyPay.",
        "title": "Chyba"
      }
    },
    "emailForm": {
      "title": "Prihlásenie",
      "buttonLabel": "Prihlásenie"
    },
    "email": {
      "input": {
        "placeholder": "Zadajte e-mailovú adresu",
        "label": "E-mailová adresa"
      }
    }
  },
  "sl-SI": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Napaka"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Za ta e-poštni naslov v sistemu HubSystem ni računa DutyPay.",
          "title": "Napaka"
        }
      },
      "userDoesNotExist": {
        "message": "Za ta e-poštni naslov v sistemu HubSystem ni računa DutyPay.",
        "title": "Napaka"
      }
    },
    "emailForm": {
      "title": "Prijava",
      "buttonLabel": "Prijava"
    },
    "email": {
      "input": {
        "placeholder": "Vnesite e-poštni naslov",
        "label": "E-poštni naslov"
      }
    }
  },
  "sv-SE": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Fel"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "Det finns inget DutyPay-konto i HubSystem för den här e-postadressen.",
          "title": "Fel"
        }
      },
      "userDoesNotExist": {
        "message": "Det finns inget DutyPay-konto i HubSystem för den här e-postadressen.",
        "title": "Fel"
      }
    },
    "emailForm": {
      "title": "Logga in",
      "buttonLabel": "Logga in"
    },
    "email": {
      "input": {
        "placeholder": "Ange e-postadress",
        "label": "E-postadress"
      }
    }
  },
  "th-TH": {
    "notification": {
      "hasUser": {
        "asyncError": {}
      },
      "checkUser": {
        "asyncError": {
          "title": "ความผิดพลาด"
        }
      },
      "userDoesNotExist": {}
    },
    "emailForm": {
      "title": "เข้าสู่ระบบ",
      "buttonLabel": "เข้าสู่ระบบ"
    },
    "email": {
      "input": {
        "placeholder": "ป้อนที่อยู่อีเมล",
        "label": "ที่อยู่อีเมล"
      }
    }
  },
  "tr-CY": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Hata"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "HubSystem'de bu e-posta adresi için bir DutyPay hesabı yok.",
          "title": "Hata"
        }
      },
      "userDoesNotExist": {
        "message": "HubSystem'de bu e-posta adresi için bir DutyPay hesabı yok.",
        "title": "Hata"
      }
    },
    "emailForm": {
      "title": "Oturum aç",
      "buttonLabel": "Oturum aç"
    },
    "email": {
      "input": {
        "placeholder": "E-posta adresini giriniz",
        "label": "E-posta adresi"
      }
    }
  },
  "tr-TR": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Hata"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "HubSystem'de bu e-posta adresi için bir DutyPay hesabı yok.",
          "title": "Hata"
        }
      },
      "userDoesNotExist": {
        "message": "HubSystem'de bu e-posta adresi için bir DutyPay hesabı yok.",
        "title": "Hata"
      }
    },
    "emailForm": {
      "title": "Oturum aç",
      "buttonLabel": "Oturum aç"
    },
    "email": {
      "input": {
        "placeholder": "E-posta adresini giriniz",
        "label": "E-posta adresi"
      }
    }
  },
  "uk-UA": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "Помилка"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "У системі HubSystem немає облікового запису DutyPay для цієї електронної адреси.",
          "title": "Помилка"
        }
      },
      "userDoesNotExist": {
        "message": "У системі HubSystem немає облікового запису DutyPay для цієї електронної адреси.",
        "title": "Помилка"
      }
    },
    "emailForm": {
      "title": "Анмельден",
      "buttonLabel": "Анмельден"
    },
    "email": {
      "input": {
        "placeholder": "Введіть адресу електронної пошти",
        "label": "Електронна адреса"
      }
    }
  },
  "vi-VN": {
    "notification": {
      "hasUser": {
        "asyncError": {}
      },
      "checkUser": {
        "asyncError": {
          "title": "Lỗi"
        }
      },
      "userDoesNotExist": {}
    },
    "emailForm": {
      "title": "Đăng nhập",
      "buttonLabel": "Đăng nhập"
    },
    "email": {
      "input": {
        "placeholder": "Nhập địa chỉ email",
        "label": "Địa chỉ email"
      }
    }
  },
  "zh-CN": {
    "notification": {
      "hasUser": {
        "asyncError": {
          "title": "错误"
        }
      },
      "checkUser": {
        "asyncError": {
          "message": "该电子邮件地址在 HubSystem 中没有 DutyPay 帐户。",
          "title": "错误"
        }
      },
      "userDoesNotExist": {
        "message": "该电子邮件地址在 HubSystem 中没有 DutyPay 帐户。",
        "title": "错误"
      }
    },
    "emailForm": {
      "title": "登录",
      "buttonLabel": "登录"
    },
    "email": {
      "input": {
        "placeholder": "输入电子邮件地址",
        "label": "电子邮件地址"
      }
    }
  }
}
</i18n>
